import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

export interface NewFeaturePulsePage {
  /** Pathname'e `includes()` ile eşlenen yol parçası. */
  path: string;
  /** `false` ise bu sayfa takip edilmez (feature flag için). Varsayılan: true. */
  enabled?: boolean;
}

/**
 * Bir menüde "yeni özellik" pulse'ı gösterir ve kullanıcı verilen tüm (aktif)
 * sayfaları en az bir kez ziyaret edene kadar görünür kalır. Yeni bir sayfa
 * tanıtmak istediğinizde ilgili menüde bu hook'u çağırıp dönen değeri
 * `showPulse` olarak verin — kopyala/yapıştır gerekmez.
 *
 * Durum bellekte tutulur; tam sayfa yenilemede pulse tekrar görünür
 * (önceki flaş ürünler davranışıyla aynı).
 */
export function useNewFeaturePulse(pages: NewFeaturePulsePage[]): boolean {
  const { pathname } = useLocation();
  const visitedRef = useRef<Set<string>>(new Set());
  const [pulseVisible, setPulseVisible] = useState(true);

  // Aktif sayfaların yollarını stabil bir anahtara çeviriyoruz; böylece effect
  // bağımlılığı her render'da yeni array kimliğinden etkilenmez.
  const trackedKey = pages
    .filter((page) => page.enabled !== false)
    .map((page) => page.path)
    .join('|');

  useEffect(() => {
    if (!trackedKey) return;

    const trackedPaths = trackedKey.split('|');
    const matched = trackedPaths.find((path) => pathname.includes(path));
    if (matched) {
      visitedRef.current.add(matched);
    }

    if (trackedPaths.every((path) => visitedRef.current.has(path))) {
      setPulseVisible(false);
    }
  }, [pathname, trackedKey]);

  return pulseVisible && trackedKey.length > 0;
}
